import { router } from '@inertiajs/react'
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_QUERY_LENGTH } from '../../lib/map_config'
import { declineRateLimitDialog } from '../../lib/rate_limit'
import type { SearchResults } from '../../types/atlas'

interface SearchBoxProps {
  results?: SearchResults
  onSelectPlace: (slug: string) => void
  onSelectGrape: (slug: string) => void
  onSelectWinery: (slug: string) => void
  /**
   * The result a reader is about to choose — the active option — or null when
   * there is none. Told when it CHANGES, so the shell can fetch that page
   * before Enter or the click. Optional: the box works without anyone
   * listening.
   */
  onIntent?: (selection: Selection | null) => void
}

// One flat list over three groups, in the order they are rendered. The arrow
// keys index into THIS, so a group added beside it rather than through it is a
// group no keyboard can reach — and MapLibre's canvas is unreachable by keyboard
// already, which leaves this box as one of the only two ways to drive the atlas.
export type Selection =
  | { type: 'place'; slug: string }
  | { type: 'grape'; slug: string }
  | { type: 'winery'; slug: string }

const LISTBOX_ID = 'atlas-search-results'

const optionId = (index: number) => `atlas-search-option-${index}`

/**
 * R11's one box, over places, grapes and wineries.
 *
 * It has no endpoint of its own. An Inertia partial reload targets the page it
 * is already on, so `router.reload({ only: ['search'], data: { q } })` asks
 * whatever route the reader is standing on for one more prop — which is why the
 * box works identically on `/`, `/places/:slug` and `/grapes/:slug` without any
 * of them knowing about it. `preserveUrl` keeps the query out of the address
 * bar: a query string is not a deep link here (KTD15), and one left behind would
 * be a URL that stops working the moment it is reloaded, because the `search`
 * prop is `optional` and a plain GET never computes it.
 *
 * Because responses can land out of order, the server echoes the query it
 * answered and anything that does not match what is in the input is dropped
 * rather than shown under the wrong word. While the answer for the current word
 * is outstanding the dropdown stays open on placeholder rows — it used to
 * unmount and remount, so every keystroke past the debounce blinked the list —
 * and the previous answer is never what fills the gap.
 *
 * NOTHING IS SHOWN FROM THE CLIENT'S OWN DATA WHILE THE ANSWER IS ON ITS WAY,
 * though the shell holds every country's name and could match them in the same
 * keystroke. It was considered and left out, because it cannot be made to
 * agree with the answer that follows. The server matches a folded substring
 * against names AND aliases, ranks every kind of place together by length,
 * and keeps eight; a country matched here would sit at the top of the list for
 * a tenth of a second and then move down it, or leave it, when the real
 * ranking arrived. A row that appears and jumps is the blink this box was
 * rebuilt to remove. The wait itself is what got shorter instead
 * (`SEARCH_DEBOUNCE_MS`).
 *
 * Keyboard first: arrow keys move the selection, Enter commits it, Escape closes
 * the list, and focus never leaves the input. MapLibre's canvas is unreachable
 * by keyboard, so this box and the layout's atlas list are the only ways to
 * drive the atlas without a pointer.
 */
export default function SearchBox({
  results,
  onSelectPlace,
  onSelectGrape,
  onSelectWinery,
  onIntent,
}: SearchBoxProps) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [dismissed, setDismissed] = useState(false)
  // The last word a request FINISHED for, whether or not it brought an answer.
  // It exists only to end the placeholder rows when a request fails: a search
  // that errors fires no success and changes no prop, and rows that pulse
  // forever would be promising an answer that is not coming.
  const [settled, setSettled] = useState<string | null>(null)

  const term = query.trim()
  const input = useRef<HTMLInputElement>(null)

  // "/" puts the caret in the box, from anywhere on the page that is not
  // already somewhere to type — the key every search field on the web answers
  // to, and the pill says so (the hint beside the placeholder). One listener
  // on the document for the life of the box; it reads the key and the target
  // and does nothing else.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return

      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return

      event.preventDefault()
      input.current?.focus()
    }

    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (term.length < SEARCH_MIN_QUERY_LENGTH) return

    const timer = setTimeout(() => {
      router.reload({
        only: ['search'],
        data: { q: term },
        preserveUrl: true,
        // Runs after the page props have been swapped in, so on success the
        // answer is already on screen by the time this lands and it changes
        // nothing the reader can see.
        onFinish: () => setSettled(term),
        // A keystroke the server refused as one too many must not put a
        // full-screen dialog over the map. Declining it leaves `onFinish` to
        // end the placeholder rows, and the box then shows what it shows for
        // any search that did not answer: nothing, until the word is typed
        // again — which is also the retry.
        onHttpException: declineRateLimitDialog,
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [term])

  // ONE LETTER IS NOT A SEARCH, AND THE BOX SAYS SO. Below the minimum length
  // nothing is requested — every row in three tables would match — and the last
  // answer used to stand in the list meanwhile. When that answer was "nothing"
  // the box showed "No matches" under a single "a", which is false: it had not
  // looked. So a short word shows its own line and never somebody else's
  // answer.
  const short = term.length > 0 && term.length < SEARCH_MIN_QUERY_LENGTH

  // A result set the reader has typed past.
  const stale = term.length >= SEARCH_MIN_QUERY_LENGTH && results?.query !== term
  const shown = results && !stale && !short && !dismissed ? results : null

  // An answer is on its way for what is in the box: typed past the minimum, not
  // yet answered, not waved away, and the request has not already come back
  // empty-handed. This starts with the keystroke rather than with the request,
  // so the debounce reads as the search working instead of as a dead interval.
  const searching = stale && !dismissed && settled !== term
  const selections: Selection[] = shown
    ? [
        ...shown.places.map((place): Selection => ({ type: 'place', slug: place.slug })),
        ...shown.grapes.map((grape): Selection => ({ type: 'grape', slug: grape.slug })),
        ...shown.wineries.map((winery): Selection => ({ type: 'winery', slug: winery.slug })),
      ]
    : []

  // The list is open whenever there is an answer to show — including an answer of
  // "nothing", which the reader has to be told rather than left guessing at. The
  // empty state reads off the *answered* query, not the input: a reader who
  // cleared the box has not just searched for nothing.
  const listShown = Boolean(shown) && (selections.length > 0 || (shown?.query.length ?? 0) >= SEARCH_MIN_QUERY_LENGTH)

  useEffect(() => {
    setActive(0)
  }, [results])

  // The active option is what Enter would open, and with a pointer it is the
  // row under it — so it is the best guess the box has at what comes next, and
  // the shell is told each time the guess changes. Keyed on what the option IS
  // rather than on its index: a new answer whose first row is the same place
  // is the same intention.
  const intended = listShown ? selections[active] : undefined
  const intendedKey = intended ? `${intended.type}:${intended.slug}` : null
  useEffect(() => {
    onIntent?.(intended ?? null)
    // `intended` is rebuilt on every render; its key is what identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intendedKey, onIntent])

  const commit = useCallback(
    (selection: Selection) => {
      setQuery('')
      setDismissed(true)

      if (selection.type === 'place') onSelectPlace(selection.slug)
      else if (selection.type === 'grape') onSelectGrape(selection.slug)
      else onSelectWinery(selection.slug)
    },
    [onSelectGrape, onSelectPlace, onSelectWinery],
  )

  // Every key that moves or commits is prevented: ArrowUp would otherwise send
  // the caret to the start of the input, and Enter would submit a form if this
  // box is ever mounted inside one.
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      // Prevented because this is an `input[type=search]`, and Chrome's default
      // for Escape on one is to CLEAR it. That fires `onChange`, which resets
      // `dismissed` — so the list the reader just dismissed came straight back,
      // over an input they had not asked to be emptied. jsdom does not implement
      // that default, which is why only the browser test sees it.
      event.preventDefault()

      // Swallowed only while there is a list to dismiss. `DetailPanel` listens
      // for Escape on `document`, and React attaches its own handlers on the
      // root container below that -- so on `/places/:slug` a reader who opened
      // a place, typed in this box, then pressed Escape to get rid of the
      // dropdown was navigated off the page and lost the panel they were
      // reading. With nothing open the key belongs to the panel, and it still
      // reaches it. The placeholder rows count as something open: a reader who
      // sees a dropdown and presses Escape means the dropdown.
      if (listShown || searching || (short && !dismissed)) event.stopPropagation()
      setDismissed(true)
      return
    }

    if (selections.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => Math.min(index + 1, selections.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      commit(selections[active])
    }
  }

  let index = -1

  const option = (key: string, selection: Selection, label: string, detail: string) => {
    index += 1
    const position = index

    return (
      <div
        key={key}
        id={optionId(position)}
        role="option"
        aria-selected={position === active}
        // Focus has to stay in the input for `aria-activedescendant` to mean
        // anything, so the press that would move it never happens.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => commit(selection)}
        onMouseEnter={() => setActive(position)}
        // 44px under a thumb, 40px under a pointer.
        className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm md:min-h-10 ${
          position === active ? 'bg-accent/10' : ''
        }`}
      >
        {/* The name gives way before the qualifier does: two wineries that share
            a name are told apart by the place, so the place is what must not be
            squeezed to nothing — but it is capped, so a long one cannot take
            the row from the name either. */}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className="max-w-[45%] shrink-0 truncate text-[11px] uppercase tracking-wide text-ink-soft">
          {detail}
        </span>
      </div>
    )
  }

  const groupLabel = (text: string) => (
    <p
      aria-hidden="true"
      className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft"
    >
      {text}
    </p>
  )

  return (
    // `md:relative`, and deliberately NOT positioned below `md`. The results
    // hang off the nearest positioned ancestor: from `md` that is this box, so
    // they sit under the field; on a phone it is the header's cluster, so they
    // span the whole row instead of the 260px the field is left with once the
    // two buttons have taken their 44px each.
    //
    // From `md` it is 20rem and GIVES WAY: it may shrink to 11rem before
    // anything beside it has to wrap. At a fixed 20rem the bar was 23px too
    // wide for a 768px window, and the nav wrapped alone to a second row.
    <div className="pointer-events-auto min-w-0 flex-1 md:relative md:w-80 md:min-w-44 md:max-w-full md:flex-initial">
      {/* A `<label>`, so a press anywhere on the pill — the wordmark, the icon,
          the padding — lands in the field. The field's accessible name is still
          its own `aria-label`, which takes precedence over the label's text. */}
      <label className="flex h-11 items-center gap-2.5 rounded-full bg-surface-raised pl-4 pr-3 shadow-chrome ring-1 ring-black/5 focus-within:ring-2 focus-within:ring-accent/60 md:h-10">
        {/* The wordmark lives INSIDE the pill from `md` up: one surface where
            there were two, and the display face is on screen from first paint,
            so the first panel title does not swap fonts when it opens. Hidden
            on a phone, where the row belongs to the field. */}
        <span className="hidden shrink-0 font-display text-[17px] font-semibold tracking-tight text-ink md:block">
          Winemap
        </span>
        <span aria-hidden="true" className="hidden h-4 w-px shrink-0 bg-black/10 md:block" />
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="h-4 w-4 shrink-0 text-ink-soft"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="m14 14 4 4" />
        </svg>
        <input
          type="search"
          role="combobox"
          aria-label="Search places, grapes and wineries"
          aria-expanded={listShown}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={selections.length > 0 ? optionId(active) : undefined}
          ref={input}
          autoComplete="off"
          // What is typed here is a name — Aglianico, Châteauneuf, Wiens — and a
          // dictionary underlines most of them in red, capitalises them and, on
          // a phone, corrects them into other words.
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          // Short enough to read whole at every width. The long form is the
          // field's accessible name above; as a placeholder it was cut off
          // mid-word ("…and winerie") at both the widths this is used at.
          placeholder="Search the atlas"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setDismissed(false)
            // A word that failed once is asked for again when it is retyped,
            // so it gets its placeholder rows again too.
            setSettled(null)
          }}
          onKeyDown={onKeyDown}
          // `text-base` below `md` is 16px, and that number is the point: iOS
          // Safari zooms the page when a field under 16px takes focus, and on a
          // map that is `h-dvh overflow-hidden` there is no gesture that zooms
          // it back out. The border, ring and outline are zeroed because the
          // pill draws the focus state (`focus-within` above), and the
          // `@tailwindcss/forms` defaults would draw a grey box and a blue ring
          // inside it.
          className="peer min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-ink placeholder:text-ink-soft focus:outline-none focus:ring-0 md:text-sm [&::-webkit-search-cancel-button]:appearance-none"
        />
        {/* The key that focuses the box, shown while the box is empty and
            unfocused, and where there is a keyboard to press it on — and room:
            from `lg`, because on a tablet beside an open panel the field is
            six letters wide and the hint took two of them. */}
        <kbd
          aria-hidden="true"
          className="hidden h-5 min-w-5 shrink-0 place-items-center rounded border border-black/10 px-1 font-sans text-[11px] text-ink-soft peer-[:placeholder-shown:not(:focus)]:lg:grid"
        >
          /
        </kbd>
      </label>

      {/* ONE frame for both states, so it is mounted once per search rather
          than once per answer: the results replace the placeholder rows inside
          a surface that is already there, and the entrance animation does not
          replay on every keystroke.

          The height is bounded by what the header is guaranteed below `md` —
          40dvh, because an open detail sheet takes the other 60 and the header
          clips at its edge — less the bar the list hangs under. A list taller
          than that would lose its last rows behind the sheet with no way to
          scroll to them. */}
      {(listShown || searching || (short && !dismissed)) && (
        <div className="absolute inset-x-0 top-full z-20 mt-2 max-h-[min(24rem,calc(40dvh-5rem))] overflow-y-auto overscroll-contain rounded-2xl bg-surface-raised p-1.5 text-ink shadow-panel ring-1 ring-black/5 motion-safe:animate-chrome-in md:right-auto md:max-h-[min(24rem,60dvh)] md:w-[22rem]">
          {listShown && shown ? (
            <div id={LISTBOX_ID} role="listbox" aria-label="Search results">
              {shown.places.length > 0 && (
                <div role="group" aria-label="Places">
                  {groupLabel('Places')}
                  {shown.places.map((place) =>
                    option(`place-${place.slug}`, { type: 'place', slug: place.slug }, place.name, place.kind),
                  )}
                </div>
              )}

              {shown.grapes.length > 0 && (
                <div role="group" aria-label="Grapes">
                  {groupLabel('Grapes')}
                  {/* One entry per name, not per row: the source is one row per
                      FPS clone, so "Cabernet Sauvignon" is 73 rows and listing
                      them all would be a list nobody can choose from. */}
                  {shown.grapes.map((grape) =>
                    option(
                      `grape-${grape.slug}`,
                      { type: 'grape', slug: grape.slug },
                      grape.name,
                      // What it IS. It read "3 records" where the register
                      // lists a variety once per clone — a count of rows in a
                      // table, where the two groups beside this one say what
                      // kind of place and where a winery is.
                      'Grape',
                    ),
                  )}
                </div>
              )}

              {shown.wineries.length > 0 && (
                <div role="group" aria-label="Wineries">
                  {groupLabel('Wineries')}
                  {/* The place a producer sits in, which is what tells two
                      same-named wineries apart. P1 imported appellation
                      boundaries for the United States only, so most of them
                      fall back to a country and some to nothing at all. */}
                  {shown.wineries.map((winery) =>
                    option(
                      `winery-${winery.slug}`,
                      { type: 'winery', slug: winery.slug },
                      winery.name,
                      winery.place ?? 'Winery',
                    ),
                  )}
                </div>
              )}

              {selections.length === 0 && (
                <p data-testid="search-empty" className="px-3 py-2.5 text-sm text-ink-soft">
                  No matches
                </p>
              )}
            </div>
          ) : short ? (
            <p data-testid="search-short" role="status" className="px-3 py-2.5 text-sm text-ink-soft">
              Keep typing…
            </p>
          ) : (
            // NOT a listbox, and not the previous answer dimmed. The rule this
            // box has always kept is that a result is never shown under a word
            // it does not answer; placeholder rows keep that rule and still say
            // at once that the keystroke was heard.
            <>
              <div aria-hidden="true" className="space-y-1">
                <div className="h-11 rounded-lg bg-black/5 motion-safe:animate-pulse md:h-10" />
                <div className="h-11 rounded-lg bg-black/5 motion-safe:animate-pulse md:h-10" />
                <div className="h-11 rounded-lg bg-black/5 motion-safe:animate-pulse md:h-10" />
              </div>
              <p role="status" className="sr-only">
                Searching…
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
